import { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(_error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            <div className="panel p-8">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">⚠️</div>
                <h1 className="text-2xl font-bold text-[var(--color-text-primary)] mb-2">
                  Er ging iets mis
                </h1>
                <p className="text-[var(--color-text-secondary)]">
                  De applicatie heeft een onverwachte fout aangetroffen
                </p>
              </div>

              {this.state.error && (
                <div className="mb-6 p-4 bg-brand-red/10 border border-brand-red rounded-lg">
                  <p className="text-brand-red text-sm font-mono">
                    {this.state.error.toString()}
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={this.handleReset}
                  className="btn-primary"
                >
                  Herlaad Applicatie
                </button>
                <button
                  onClick={() => window.history.back()}
                  className="btn-secondary"
                >
                  Ga Terug
                </button>
              </div>

              {import.meta.env.DEV && this.state.errorInfo && (
                <details className="mt-6 p-4 bg-[var(--color-surface-alt)] rounded-lg border border-[var(--color-border)]">
                  <summary className="text-sm text-[var(--color-text-secondary)] cursor-pointer mb-2">
                    Debug Informatie
                  </summary>
                  <pre className="text-xs text-[var(--color-text-secondary)] overflow-auto max-h-64">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}

              <div className="mt-6 text-center">
                <p className="text-sm text-[var(--color-text-secondary)]">
                  Als dit probleem zich blijft voordoen, neem dan contact op met support
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
